import React from 'react'

import {
  toast as sonnerToast,
  Toaster as SonnerToaster,
  type ToasterProps as SonnerToasterProps
} from 'sonner'
import { tv, type VariantProps } from 'tailwind-variants'

import { cn } from '@/utils'

export const toastVariants = tv({
  slots: {
    containerSlot:
      'shadow-flat flex items-center overflow-hidden rounded-sm border border-solid',
    stripeSlot: 'w-1 shrink-0 self-stretch',
    contentSlot: 'flex items-center gap-4',
    iconWrapperSlot:
      'flex size-8 shrink-0 items-center justify-center rounded-full',
    iconSlot: 'material-symbols-rounded text-body-lg',
    titleSlot: 'leading-default font-semibold',
    subtitleSlot: 'leading-default whitespace-nowrap'
  },
  variants: {
    variant: {
      error: {
        containerSlot: 'bg-error-bg border-error-stroke',
        stripeSlot: 'bg-error-stroke',
        iconWrapperSlot: 'bg-error-stroke',
        iconSlot: 'text-error-icon',
        titleSlot: 'text-error-title',
        subtitleSlot: 'text-error-body'
      },
      information: {
        containerSlot: 'bg-info-bg border-info-stroke',
        stripeSlot: 'bg-info-stroke',
        iconWrapperSlot: 'bg-info-stroke',
        iconSlot: 'text-info-icon',
        titleSlot: 'text-info-title',
        subtitleSlot: 'text-info-body'
      },
      success: {
        containerSlot: 'bg-success-bg border-success-stroke',
        stripeSlot: 'bg-success-stroke',
        iconWrapperSlot: 'bg-success-stroke',
        iconSlot: 'text-success-icon',
        titleSlot: 'text-success-title',
        subtitleSlot: 'text-success-body'
      },
      warning: {
        containerSlot: 'bg-warning-bg border-warning-stroke',
        stripeSlot: 'bg-warning-stroke',
        iconWrapperSlot: 'bg-warning-stroke',
        iconSlot: 'text-warning-icon',
        titleSlot: 'text-warning-title',
        subtitleSlot: 'text-warning-body'
      }
    },
    size: {
      md: {
        containerSlot: 'min-h-20 w-fit gap-6 pr-6',
        titleSlot: 'font-heading text-title-lg',
        subtitleSlot: 'font-body text-body-md font-normal'
      },
      sm: {
        containerSlot: 'min-h-[55px] w-fit gap-3 pr-6',
        titleSlot: 'font-body text-body-lg font-bold',
        subtitleSlot: 'font-body text-caption-lg font-semibold'
      }
    }
  },
  defaultVariants: {
    variant: 'information',
    size: 'md'
  } as const
})

export type TToastProps = Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> &
  VariantProps<typeof toastVariants> & {
    icon?: string
    subtitle: React.ReactNode
    title: string
    width?: 'fit' | 'full' | string
  }

export const Toast = ({
  icon,
  title,
  subtitle,
  size,
  variant,
  width
}: TToastProps): JSX.Element => {
  const {
    containerSlot,
    stripeSlot,
    contentSlot,
    iconWrapperSlot,
    iconSlot,
    titleSlot,
    subtitleSlot
  } = toastVariants({ variant, size })

  const getWidthStyle = ():
    | { className?: string; style?: React.CSSProperties }
    | Record<string, never> => {
    if (!width) return {}

    if (width === 'fit') {
      return { className: 'w-fit min-w-84 max-w-[600px]' }
    }

    if (width === 'full') {
      return { className: 'w-full' }
    }

    return { style: { width } }
  }

  const { className: widthClass, style: widthStyle } = getWidthStyle()

  return (
    <div className={cn(containerSlot(), widthClass)} style={widthStyle}>
      <div className={stripeSlot()} />
      <div className={contentSlot()}>
        {icon && (
          <div className={iconWrapperSlot()}>
            <span className={iconSlot()}>{icon}</span>
          </div>
        )}
        <div className='flex flex-col'>
          <p className={titleSlot()}>{title}</p>
          <div
            className={cn(
              subtitleSlot(),
              typeof subtitle !== 'string' && 'whitespace-normal'
            )}
          >
            {subtitle}
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * @deprecated Use `useToast` from `@alternative/acqua/organisms` instead.
 * The acqua-ui Sonner-based Toast is no longer aligned with the Aqua design
 * system and will be removed in a future major version. New code should
 * import the BaseUI-backed Toast from `@alternative/acqua`; existing call
 * sites should migrate when touched.
 */
export const toast = (
  toast: TToastProps,
  options?: SonnerToasterProps
): ReturnType<typeof sonnerToast.custom> =>
  sonnerToast.custom(() => <Toast {...toast} />, {
    ...options,
    duration: options?.duration ?? 3000,
    position: options?.position ?? 'top-center',
    style: {
      width: 'max-content',
      ...((options as { style?: object })?.style ?? {})
    }
  })

/**
 * @deprecated Use `Toaster` from `@alternative/acqua/organisms` instead.
 * The acqua-ui Sonner-based Toaster is no longer aligned with the Aqua design
 * system and will be removed in a future major version.
 */
export const Toaster = (props: SonnerToasterProps): JSX.Element => (
  <SonnerToaster expand visibleToasts={5} {...props} />
)
